import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState({ description: '', unitPrice: '', quantity: 1 });

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const data = await api.get(`/invoices/${id}`);
      setInvoice(data.invoice);
    } catch (error) {
      toast.error('Failed to load invoice');
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    try {
      await api.post(`/invoices/${id}/send`);
      toast.success('Invoice sent to patient');
      fetchInvoice();
    } catch (error) {
      toast.error(error.error || 'Failed to send invoice');
    }
  };

  const handleMarkPaid = async () => {
    try {
      await api.put(`/invoices/${id}/pay`, { paymentMethod });
      toast.success('Invoice marked as paid');
      setShowPaymentModal(false);
      fetchInvoice();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success('Invoice deleted');
      navigate('/invoices');
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const handleAddItem = async () => {
    if (!newItem.description || !newItem.unitPrice) {
      toast.error('Please enter description and price');
      return;
    }
    try {
      await api.post(`/invoices/${id}/items`, {
        description: newItem.description,
        unitPrice: parseFloat(newItem.unitPrice),
        quantity: parseInt(newItem.quantity) || 1
      });
      toast.success('Item added');
      setNewItem({ description: '', unitPrice: '', quantity: 1 });
      setShowAddItem(false);
      fetchInvoice();
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const handleDeleteItem = async (itemIndex) => {
    try {
      await api.delete(`/invoices/${id}/items/${itemIndex}`);
      toast.success('Item removed');
      fetchInvoice();
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return '#22c55e';
      case 'sent': return '#3b82f6';
      case 'overdue': return '#ef4444';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div className="loading-spinner"></div>
        <p>Loading invoice...</p>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Invoice not found</p>
        <Link to="/invoices" className="btn btn-primary">Back to Invoices</Link>
      </div>
    );
  }

  const clinic = user?.clinic || {};
  const patient = invoice.patient || {};
  const items = invoice.items || [];
  const outstandingBalance = invoice.status !== 'paid' ? parseFloat(invoice.total || 0) : 0;

  return (
    <div className="invoice-detail-page">
      <style>{`
        .invoice-detail-page {
          max-width: 900px;
          margin: 0 auto;
          padding: 1.5rem;
        }
        .invoice-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          flex-wrap: wrap;
          gap: 1rem;
        }
        .invoice-title {
          font-size: 1.75rem;
          font-weight: 600;
          color: #1a1a2e;
        }
        .invoice-actions {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .invoice-actions button {
          padding: 0.5rem 1rem;
          border: 1px solid #e5e7eb;
          background: white;
          border-radius: 6px;
          font-size: 0.875rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.4rem;
          transition: all 0.2s;
        }
        .invoice-actions button:hover {
          background: #f3f4f6;
          border-color: #d1d5db;
        }
        .invoice-actions button.primary {
          background: #10b981;
          color: white;
          border-color: #10b981;
        }
        .invoice-actions button.primary:hover {
          background: #059669;
        }
        .invoice-actions button.danger:hover {
          background: #fef2f2;
          border-color: #ef4444;
          color: #ef4444;
        }
        .breadcrumb {
          font-size: 0.875rem;
          color: #6b7280;
          margin-bottom: 1.5rem;
        }
        .breadcrumb a {
          color: #10b981;
          text-decoration: none;
        }
        .breadcrumb a:hover {
          text-decoration: underline;
        }
        .alert-banner {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem 1.5rem;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          margin-bottom: 1.5rem;
        }
        .alert-icon {
          width: 40px;
          height: 40px;
          background: #10b981;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.25rem;
        }
        .invoice-body {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1.5rem;
        }
        .invoice-top {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 2rem;
          margin-bottom: 2rem;
        }
        @media (max-width: 768px) {
          .invoice-top {
            grid-template-columns: 1fr;
          }
        }
        .invoice-section-label {
          color: #3b82f6;
          font-size: 0.875rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }
        .invoice-section-content {
          font-size: 0.9rem;
          color: #374151;
          line-height: 1.6;
        }
        .invoice-details-table {
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          overflow: hidden;
        }
        .invoice-details-row {
          display: flex;
          border-bottom: 1px solid #e5e7eb;
        }
        .invoice-details-row:last-child {
          border-bottom: none;
        }
        .invoice-details-label {
          width: 50%;
          padding: 0.6rem 1rem;
          background: #f9fafb;
          color: #3b82f6;
          font-size: 0.8rem;
          font-weight: 500;
        }
        .invoice-details-value {
          width: 50%;
          padding: 0.6rem 1rem;
          font-size: 0.85rem;
          text-align: right;
        }
        .invoice-details-value.highlight {
          color: #10b981;
        }
        .items-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 1.5rem;
        }
        .items-table th {
          background: white;
          color: #10b981;
          font-weight: 600;
          font-size: 0.8rem;
          text-align: left;
          padding: 0.75rem 1rem;
          border-bottom: 2px solid #e5e7eb;
        }
        .items-table td {
          padding: 1rem;
          border-bottom: 1px solid #e5e7eb;
          font-size: 0.9rem;
        }
        .items-table tr:last-child td {
          border-bottom: none;
        }
        .totals-section {
          margin-top: 1.5rem;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
        }
        .totals-row {
          display: flex;
          justify-content: space-between;
          width: 300px;
          padding: 0.5rem 0;
          font-size: 0.9rem;
        }
        .totals-row.label {
          color: #3b82f6;
        }
        .totals-row.outstanding {
          font-weight: 700;
          font-size: 1.1rem;
          padding-top: 1rem;
          margin-top: 0.5rem;
          border-top: 1px solid #e5e7eb;
        }
        .enter-payment-btn {
          margin-top: 1rem;
          width: 300px;
          padding: 0.75rem 1.5rem;
          background: #10b981;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .enter-payment-btn:hover {
          background: #059669;
        }
        .enter-payment-btn:disabled {
          background: #d1d5db;
          cursor: not-allowed;
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal-content {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          width: 100%;
          max-width: 400px;
        }
        .modal-title {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 1.5rem;
        }
        @media print {
          .invoice-actions, .enter-payment-btn, .alert-banner { display: none !important; }
          .invoice-detail-page { padding: 0; }
        }
      `}</style>

      <div className="invoice-header">
        <h1 className="invoice-title">Invoice #{invoice.invoiceNumber}</h1>
        <div className="invoice-actions">
          {invoice.status !== 'paid' && (
            <button className="primary" onClick={() => setShowPaymentModal(true)}>
              <span>Pay</span>
            </button>
          )}
          <button onClick={() => navigate(`/patients/${patient.id}`)}>
            <span>+ Book appt.</span>
          </button>
          <button onClick={handlePrint}>
            <span>Print</span>
          </button>
          <button onClick={handleSendEmail}>
            <span>Email</span>
          </button>
          <button className="danger" onClick={handleDelete}>
            <span>Delete</span>
          </button>
        </div>
      </div>

      <div className="breadcrumb">
        <Link to="/invoices">Invoices</Link> &gt; #{invoice.invoiceNumber}
      </div>

      {invoice.status !== 'paid' && outstandingBalance > 0 && (
        <div className="alert-banner">
          <div className="alert-icon">!</div>
          <div>
            {patient.firstName} {patient.lastName} has an <strong>outstanding account balance of £{outstandingBalance.toFixed(2)}</strong>
          </div>
        </div>
      )}

      <div className="invoice-body">
        <div className="invoice-top">
          <div>
            <div className="invoice-section-label">From</div>
            <div className="invoice-section-content">
              <strong>{clinic.name || 'Your Clinic'}</strong><br />
              {clinic.address || '123 High Street'}<br />
              {clinic.city || 'London'}
            </div>
          </div>

          <div>
            <div className="invoice-section-label">Billed to</div>
            <div className="invoice-section-content">
              <strong>{patient.firstName} {patient.lastName}</strong><br />
              {patient.address || ''}<br />
              {patient.city || ''}
            </div>
          </div>

          <div className="invoice-details-table">
            <div className="invoice-details-row">
              <div className="invoice-details-label">Invoice date</div>
              <div className="invoice-details-value">{formatDate(invoice.invoiceDate)}</div>
            </div>
            <div className="invoice-details-row">
              <div className="invoice-details-label">Tax Invoice</div>
              <div className="invoice-details-value">{invoice.invoiceNumber}</div>
            </div>
            <div className="invoice-details-row">
              <div className="invoice-details-label">Patient</div>
              <div className="invoice-details-value highlight">{patient.firstName} {patient.lastName}</div>
            </div>
            <div className="invoice-details-row">
              <div className="invoice-details-label">Appointment date</div>
              <div className="invoice-details-value highlight">{formatDate(invoice.appointmentDate)}</div>
            </div>
            <div className="invoice-details-row">
              <div className="invoice-details-label">Practitioner</div>
              <div className="invoice-details-value">{invoice.practitionerName || user?.firstName + ' ' + user?.lastName}</div>
            </div>
          </div>
        </div>

        <table className="items-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Unit price</th>
              <th>Quantity</th>
              <th style={{ textAlign: 'right' }}>Total price</th>
              {invoice.status !== 'paid' && <th style={{ width: '40px' }}></th>}
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index}>
                <td>{item.description}</td>
                <td>£{parseFloat(item.unitPrice || 0).toFixed(2)}</td>
                <td>{item.quantity}</td>
                <td style={{ textAlign: 'right' }}>£{parseFloat(item.total || item.quantity * item.unitPrice).toFixed(2)}</td>
                {invoice.status !== 'paid' && (
                  <td>
                    <button 
                      onClick={() => handleDeleteItem(index)}
                      style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '1rem' }}
                    >
                      ×
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={invoice.status !== 'paid' ? 5 : 4} style={{ textAlign: 'center', color: '#6b7280', padding: '1rem' }}>
                  No items yet. Add an item below.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {invoice.status !== 'paid' && (
          <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
            {!showAddItem ? (
              <button 
                onClick={() => setShowAddItem(true)}
                style={{ 
                  background: '#10b981', 
                  color: 'white', 
                  border: 'none', 
                  padding: '0.5rem 1rem', 
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
              >
                + Add Item
              </button>
            ) : (
              <div style={{ 
                display: 'flex', 
                gap: '0.5rem', 
                alignItems: 'flex-end',
                flexWrap: 'wrap',
                padding: '1rem',
                background: '#f9fafb',
                borderRadius: '8px'
              }}>
                <div style={{ flex: 2, minWidth: '150px' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem', color: '#374151' }}>Description</label>
                  <input 
                    type="text"
                    className="form-input"
                    placeholder="e.g. Initial Assessment"
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: '80px' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem', color: '#374151' }}>Price (£)</label>
                  <input 
                    type="number"
                    className="form-input"
                    placeholder="0.00"
                    step="0.01"
                    value={newItem.unitPrice}
                    onChange={(e) => setNewItem({ ...newItem, unitPrice: e.target.value })}
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ flex: 0.5, minWidth: '60px' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem', color: '#374151' }}>Qty</label>
                  <input 
                    type="number"
                    className="form-input"
                    min="1"
                    value={newItem.quantity}
                    onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                    style={{ width: '100%' }}
                  />
                </div>
                <button 
                  onClick={handleAddItem}
                  style={{ 
                    background: '#10b981', 
                    color: 'white', 
                    border: 'none', 
                    padding: '0.5rem 1rem', 
                    borderRadius: '6px',
                    cursor: 'pointer',
                    height: '38px'
                  }}
                >
                  Add
                </button>
                <button 
                  onClick={() => { setShowAddItem(false); setNewItem({ description: '', unitPrice: '', quantity: 1 }); }}
                  style={{ 
                    background: '#e5e7eb', 
                    color: '#374151', 
                    border: 'none', 
                    padding: '0.5rem 1rem', 
                    borderRadius: '6px',
                    cursor: 'pointer',
                    height: '38px'
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        <div className="totals-section">
          {parseFloat(invoice.vat) > 0 && (
            <div className="totals-row label">
              <span>Tax</span>
              <span>£{parseFloat(invoice.vat).toFixed(2)}</span>
            </div>
          )}
          <div className="totals-row label">
            <span>Invoice amount</span>
            <span>£{parseFloat(invoice.total).toFixed(2)}</span>
          </div>
          <div className="totals-row outstanding">
            <span>Outstanding balance</span>
            <span>£{outstandingBalance.toFixed(2)}</span>
          </div>

          {invoice.status !== 'paid' && (
            <button className="enter-payment-btn" onClick={() => setShowPaymentModal(true)}>
              <span>Enter payment</span>
              <span>&gt;</span>
            </button>
          )}

          {invoice.status === 'paid' && (
            <div style={{ 
              marginTop: '1rem', 
              padding: '0.75rem 1.5rem', 
              background: '#dcfce7', 
              color: '#166534',
              borderRadius: '6px',
              fontWeight: 600,
              width: '300px',
              textAlign: 'center'
            }}>
              PAID
            </div>
          )}
        </div>
      </div>

      {showPaymentModal && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2 className="modal-title">Enter Payment</h2>
            <div className="form-group">
              <label className="form-label">Payment Method</label>
              <select 
                className="form-input"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <option value="card">Card</option>
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cheque">Cheque</option>
              </select>
            </div>
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label className="form-label">Amount</label>
              <input 
                type="text" 
                className="form-input" 
                value={`£${parseFloat(invoice.total).toFixed(2)}`} 
                disabled 
              />
            </div>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button 
                className="btn btn-secondary" 
                style={{ flex: 1 }}
                onClick={() => setShowPaymentModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                style={{ flex: 1 }}
                onClick={handleMarkPaid}
              >
                Confirm Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InvoiceDetail;
